import React from 'react';
import { BarChart, Trash2, Layers } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных планов накоплений
 */
const ComparisonSidebar = ({ 
  savedPlans, 
  deleteSavedPlan, 
  loadSavedPlan 
}) => {
  // Если нет сохраненных планов
  if (!savedPlans || savedPlans.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-blue-500" />
          Сравнение планов
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные варианты планов накоплений для сравнения.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-blue-500" />
          Сравнение планов
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedPlans.length} {savedPlans.length === 1 ? 'вариант' : savedPlans.length < 5 ? 'варианта' : 'вариантов'}
        </span>
      </div>

      <div className="space-y-4">
        {savedPlans.map((plan) => (
          <div 
            key={plan.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {plan.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedPlan(plan.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedPlan(plan.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Уровень образования:</span>
                <span className="font-medium">
                  {getEducationLevelName(plan.formData.educationLevel)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Лет до начала:</span>
                <span>
                  {plan.formData.yearsUntilStart} {pluralize(parseFloat(plan.formData.yearsUntilStart), 'год', 'года', 'лет')}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Ежемесячный взнос:</span>
                <span>
                  {formatCurrency(plan.formData.monthlyContribution)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Итоговые накопления:</span>
                <span className={`font-semibold ${plan.results.isEnough ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {formatCurrency(plan.results.finalSavingsAmount)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Профицит/дефицит:</span>
                <span className={`font-semibold ${plan.results.savingsGap >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {plan.results.savingsGap >= 0 ? '+' : ''}{formatCurrency(plan.results.savingsGap)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Вспомогательная функция для получения названия уровня образования
const getEducationLevelName = (level) => {
  const names = {
    'bachelor': 'Бакалавриат',
    'specialist': 'Специалитет',
    'master': 'Магистратура',
    'postgraduate': 'Аспирантура',
    'vocational': 'Среднее проф.',
    'additional': 'Доп. образование'
  };
  
  return names[level] || level;
};

// Вспомогательная функция для склонения слов
const pluralize = (number, one, few, many) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return many;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return few;
  }
  return many;
};

export default ComparisonSidebar;